import { useEffect, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';

const PAGE_SIZE = 5;

/**
 * Paginated admin table with loading, error and empty states.
 * `columns` is a list of header labels; `renderRow(item)` returns the <tr> for one item.
 */
export default function AdminTable({ columns, items, loading, error, emptyText = 'No records found.', getKey, renderRow }) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  // Keep the current page valid when the data changes (e.g. after a refresh).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const start = (currentPage - 1) * PAGE_SIZE;
  const pageItems = items.slice(start, start + PAGE_SIZE);

  let body;
  if (loading) {
    body = <LoadingSpinner />;
  } else if (error) {
    body = <span className="text-[#981D0E]">{error}</span>;
  } else if (items.length === 0) {
    body = <span className="text-gray-500">{emptyText}</span>;
  }

  return (
    <>
      <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((label) => (
                <th key={label} className="px-6 py-3 font-medium text-gray-500">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {body ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-4 text-center">{body}</td>
              </tr>
            ) : (
              pageItems.map((item, index) => (
                <tr key={getKey(item) ?? index} className="bg-white hover:bg-gray-50">
                  {renderRow(item)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => setCurrentPage((p) => p - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>

        <div className="flex items-center gap-2">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-3 py-1 rounded text-sm font-medium ${currentPage === index + 1
                ? 'bg-[#981D0E] text-white'
                : 'border border-gray-300 hover:bg-gray-50'
                }`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        <button
          onClick={() => setCurrentPage((p) => p + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </>
  );
}

export const cellClass = 'px-6 py-4 whitespace-nowrap';
